import { create } from "zustand";
import { combine } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import {
  validateModel,
  type ModelValidationFinding,
  type ModelValidationResult,
  type ModelValidationSummary,
} from "../validation/modelValidation";
import { useSceneStore } from "./sceneStore";

export type ModelValidationStatus = "idle" | "checking" | "ready" | "error";

type ModelValidationState = {
  status: ModelValidationStatus;
  isStale: boolean;
  findings: ModelValidationFinding[];
  summary: ModelValidationSummary;
  checkedAt: number | null;
  error: string | null;
};

type ModelValidationActions = {
  run: () => ModelValidationResult | null;
  markStale: () => void;
  clear: () => void;
};

const emptySummary = (): ModelValidationSummary => ({
  errorCount: 0,
  warningCount: 0,
  infoCount: 0,
  findingCount: 0,
});

const initialState = (): ModelValidationState => ({
  status: "idle",
  isStale: false,
  findings: [],
  summary: emptySummary(),
  checkedAt: null,
  error: null,
});

const selectSceneValidationSignature = () => {
  const scene = useSceneStore.getState();
  return {
    bodies: scene.bodies,
    worldBodies: scene.worldBodies,
    joints: scene.joints,
    sites: scene.sites,
    cameras: scene.cameras,
    meshes: scene.meshes,
    hfields: scene.hfields,
    contactPairs: scene.contactPairs,
    contactExcludes: scene.contactExcludes,
    equality: scene.equality,
    tendons: scene.tendons,
    actuators: scene.actuators,
    sensors: scene.sensors,
  };
};

const sameSceneValidationSignature = (
  a: ReturnType<typeof selectSceneValidationSignature>,
  b: ReturnType<typeof selectSceneValidationSignature>
) =>
  a.bodies === b.bodies &&
  a.worldBodies === b.worldBodies &&
  a.joints === b.joints &&
  a.sites === b.sites &&
  a.cameras === b.cameras &&
  a.meshes === b.meshes &&
  a.hfields === b.hfields &&
  a.contactPairs === b.contactPairs &&
  a.contactExcludes === b.contactExcludes &&
  a.equality === b.equality &&
  a.tendons === b.tendons &&
  a.actuators === b.actuators &&
  a.sensors === b.sensors;

const useInternalModelValidationStore = create<
  ModelValidationState & { actions: ModelValidationActions }
>()(
  combine(initialState(), (set, get) => ({
    actions: {
      run: () => {
        if (get().status === "checking") return null;
        set({ status: "checking", error: null });
        try {
          const { actions: _actions, ...scene } = useSceneStore.getState();
          const result = validateModel(scene);
          set({
            status: "ready",
            isStale: false,
            findings: result.findings,
            summary: result.summary,
            checkedAt: result.checkedAt,
            error: null,
          });
          return result;
        } catch (error) {
          set({
            status: "error",
            error: String(error instanceof Error ? error.message : error),
          });
          return null;
        }
      },
      markStale: () => {
        if (get().status === "ready") set({ isStale: true });
      },
      clear: () => set(initialState()),
    },
  }))
);

let previousSceneValidationSignature = selectSceneValidationSignature();
useSceneStore.subscribe(() => {
  const next = selectSceneValidationSignature();
  if (!sameSceneValidationSignature(previousSceneValidationSignature, next)) {
    previousSceneValidationSignature = next;
    useInternalModelValidationStore.getState().actions.markStale();
  }
});

export const useModelValidationStatus = () =>
  useInternalModelValidationStore((state) => state.status);
export const useModelValidationIsStale = () =>
  useInternalModelValidationStore((state) => state.isStale);
export const useModelValidationFindings = () =>
  useInternalModelValidationStore((state) => state.findings);
export const useModelValidationSummary = () =>
  useInternalModelValidationStore(
    useShallow((state) => ({
      summary: state.summary,
      checkedAt: state.checkedAt,
      error: state.error,
    }))
  );
export const useModelValidationActions = () =>
  useInternalModelValidationStore((state) => state.actions);

export const runModelValidationFromCurrentScene = () =>
  useInternalModelValidationStore.getState().actions.run();
